import React from 'react';

import { format } from '@eika-infrastruktur/date';
import formatNumberWithCurrency from '@eika-infrastruktur/format/dist/formatNumberWithCurrency';
import { useViewType, ViewType } from '@eika/hooks';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';

import LoanActionLinks from '../../components/LoanActionLinks/LoanActionLinks';
import LoanActions from '../../components/LoanActions/LoanActions';
import { useConfig } from '../../defaultConfig';
import useAccountId from '../../hooks/useAccountId';
import { useLoanDetails } from '../../hooks/useLoans';
import { useTranslation } from '../../translations';
import { kredittbankenTrackingIDs } from '../../utils/gtm/kredittbankenTrackingIds';
import LoanAccountBalance from './components/LoanAccountBalance';
import LoanAccountDetails from './components/LoanAccountDetails';
import LoanAccountDetailsSkeleton from './components/LoanAccountDetailsSkeleton';
import LoanAccountHeader from './components/LoanAccountHeader';
import Transactions from './components/Transactions';

import './LoanOverview.scss';

const LoanOverview: React.FC = () => {
    const translated = useTranslation();
    const accountId = useAccountId();
    const { data: loanDetails, isLoading } = useLoanDetails(accountId);
    const viewType = useViewType();
    const {
        links: { detailsKredittbanken },
        featureToggles: { enableDetailsKredittbanken },
    } = useConfig();

    const initialAmount = loanDetails && loanDetails.balances.find((balance) => balance.type === 'INITIAL_PRINCIPAL');
    const availableBalance = loanDetails && loanDetails.balances.find((balance) => balance.type === 'AVAILABLE');
    const interimAmount = loanDetails && loanDetails.balances.find((balance) => balance.type === 'INTERIM_BOOKED');

    const newLoanAccount = !!(
        loanDetails &&
        loanDetails.loanPaymentStatus === 'NOT_COMPLETE' &&
        initialAmount?.amount === availableBalance?.amount &&
        interimAmount?.amount === 0
    );

    const loanPayedDown = !!(
        loanDetails &&
        loanDetails.loanPaymentStatus === 'COMPLETE' &&
        initialAmount?.amount === availableBalance?.amount &&
        interimAmount?.amount === 0
    );

    if (!loanDetails && !isLoading) return null;

    return (
        <div className="sgw-content loan-account-overview">
            <LoanAccountHeader
                isLoading={isLoading}
                loanAccount={loanDetails}
                newAccount={newLoanAccount}
                loanPayedDown={loanPayedDown}
            />
            <div className="loan-account-balance__wrapper">
                <LoanAccountBalance
                    interimAmount={interimAmount?.amount ?? 0}
                    initialAmount={initialAmount?.amount ?? 0}
                    currency={loanDetails?.currency ?? 'NOK'}
                    newAccount={newLoanAccount}
                    isLoading={isLoading}
                />
                <div className="loan-account-details-container">
                    <LoanAccountDetails
                        term={translated.loanOverview.interest}
                        description={`${loanDetails?.interestsRates.nominalInterestRate} %`}
                        isLoading={isLoading}
                        skeleton={<LoanAccountDetailsSkeleton />}
                        loanPayedDown={loanPayedDown}
                    />
                    <LoanAccountDetails
                        term={translated.loanOverview.paymentDueDate}
                        description={format(loanDetails?.billingSummary.paymentDueDate, 'd. MMMM')}
                        isLoading={isLoading}
                        skeleton={<LoanAccountDetailsSkeleton />}
                        loanPayedDown={loanPayedDown}
                    />
                    <LoanAccountDetails
                        term={translated.loanOverview.instalmentAmount}
                        description={formatNumberWithCurrency(loanDetails?.instalmentAmount)}
                        isLoading={isLoading}
                        skeleton={<LoanAccountDetailsSkeleton />}
                        loanPayedDown={loanPayedDown}
                    />
                </div>
                <div className="background-wrapper">
                    <div className="background" />
                </div>
            </div>
            {viewType !== ViewType.desktop && (
                <>
                    <LoanActions
                        accountId={accountId}
                        billingSummary={loanDetails?.billingSummary}
                        isDesktop={false}
                        isLoading={isLoading}
                    />
                    <LoanActionLinks />
                </>
            )}
            <Transactions accountId={accountId} />
            {detailsKredittbanken?.url && enableDetailsKredittbanken && (
                <div className="loan-overview-kredittbanken">
                    <p className="loan-overview-kredittbanken__info">{translated.loanOverview.infoKredittbankne}</p>
                    <Link
                        href={detailsKredittbanken?.url}
                        icon={IconTypes.EKSTERN_24}
                        iconOnRightSide
                        data-track-id={kredittbankenTrackingIDs.loanOverviewActions}
                        data-track-linkname={translated.loanOverview.showLoanAtKredittbanken}
                    >
                        {translated.loanOverview.showLoanAtKredittbanken}
                    </Link>
                </div>
            )}
        </div>
    );
};

export default LoanOverview;
